/**
 * Read-only hooks for the Enterprise SSO admin surface (EnterprisePanel).
 *
 * All four queries use the SECURITY INVOKER read RPCs added in M005, so
 * row visibility is gated by the M003 RLS policies (super_admin via
 * caller_can_manage_partner / caller_can_view_partner_audit). A non-staff
 * authenticated caller will see empty results, not an error.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

export function useEnterprisePartnerList() {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.list(),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enterprise_sso_partner_list');
      if (error) throw error;
      return data;
    },
  });
}

export function useEnterprisePartner(id: string | null) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.detail(id ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enterprise_sso_partner_get', {
        p_partner_id: id,
      });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!id,
  });
}

export function useEnterprisePartnerIdentities(partnerId: string | null) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.identities(partnerId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enterprise_sso_identity_list', {
        p_partner_id: partnerId,
        p_limit: 100,
        p_after_created_at: null,
      });
      if (error) throw error;
      return data;
    },
    enabled: !!partnerId,
  });
}

export function useEnterprisePartnerAuditLog(partnerId: string | null) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.auditLog(partnerId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('enterprise_sso_audit_log_query', {
        p_partner_id: partnerId,
        p_limit: 100,
        p_before_id: null,
      });
      if (error) throw error;
      return data;
    },
    enabled: !!partnerId,
  });
}
